/**
 * Shared embed parameter parsing utilities.
 *
 * Single source of truth for extracting embed-related URL parameters.
 * Used by both ThemeContext (to decide localStorage vs. postMessage theming)
 * and useEmbedParams (to expose embed state to the rest of the app).
 */

/**
 * Interface for raw embed URL parameters (string values from the query string).
 */
export interface EmbedParams {
  /** Whether the app is embedded in another application */
  embed?: string;
  /** Source application or context */
  source?: string;
  /** User alias for context */
  userAlias?: string;
}

/**
 * Valid keys for embed parameters.
 * Exported as const array for use in other modules.
 */
export const EMBED_PARAM_KEYS = ['embed', 'source', 'userAlias'] as const;

/**
 * Type for embed parameter keys.
 */
export type EmbedParamKey = (typeof EMBED_PARAM_KEYS)[number];

/** Internal array for type checking */
const VALID_EMBED_KEYS: Array<keyof EmbedParams> = [...EMBED_PARAM_KEYS];

/**
 * Return type for parseEmbedParams.
 */
export interface ParseEmbedParamsResult {
  /** Raw string parameters extracted from the URL */
  rawParams: EmbedParams;
  /** Whether the app is running in embedded mode */
  isEmbedded: boolean;
}

/**
 * Pure function to parse embed parameters from the current URL.
 *
 * Extracts only recognised embed keys from the query string and determines
 * whether the app is running in embedded mode (`?embed=true`).
 *
 * Safe to call during SSR or when `window.location` is unavailable — returns
 * sensible defaults on error.
 *
 * @returns Parsed raw params and an isEmbedded boolean
 */
export const parseEmbedParams = (): ParseEmbedParamsResult => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const rawParams: EmbedParams = {};

    urlParams.forEach((value, key) => {
      if (VALID_EMBED_KEYS.includes(key as keyof EmbedParams)) {
        rawParams[key as keyof EmbedParams] = value;
      }
    });

    return {
      rawParams,
      isEmbedded: urlParams.get('embed') === 'true',
    };
  } catch {
    return { rawParams: {}, isEmbedded: false };
  }
};
